import { ActionEntityType, ActionIntent, NextActionMode, OfficeActionId, } from '@office-start/action-types';
import { ActionContextMenuItemIcon, ContentActionGroup, FileType, } from '@office-start/document-types';
import { getReferralFromFileType } from '@office-start/file-utility';
import { OfficeActionCreators } from '../actionCreators/officeActionCreators';
// getAddToToDoActions and getConvertToPdfActions in this file duplicate the functionality of the same functions in m365-apps-service\src\MosAppContentActionService\getBuiltInActions.ts
// The reason to duplicate the code is to avoid loading m365-apps-service in embed workflow.
export var getAddToToDoActions = function (appId, hubActionsStrings, isHwa) {
    var documentActionsMenuResources = hubActionsStrings.documentActionsMenuResources, documentActionsToastResources = hubActionsStrings.documentActionsToastResources;
    var notificationOptions = {
        error: {
            message: documentActionsToastResources.addedToToDoFailed,
            actionText: documentActionsToastResources.closeButtonText,
        },
        loading: {
            showLoadingIndicator: false,
            loadingText: documentActionsToastResources.addingToDo,
        },
        success: {
            message: documentActionsToastResources.addedToToDo,
            actionText: documentActionsToastResources.openButtonText,
        },
    };
    var actions = [];
    actions.push(
    // Create To Do via API
    {
        intent: ActionIntent.ContentEdit,
        entities: [{ type: ActionEntityType.Content }],
        metadata: {
            appId: appId,
            groupId: ContentActionGroup.AddTo,
            name: documentActionsMenuResources.createToDoActionTitle,
            description: documentActionsMenuResources.createToDoActionTitle,
            id: OfficeActionId.CreateTodo,
            icon: {
                type: ActionContextMenuItemIcon.Svg,
                iconName: 'ToDoSvgIcon',
            },
            notificationOptions: notificationOptions,
        },
        officeActionCreator: function (content) {
            if (!content.webUrl) {
                return undefined;
            }
            var webUrl = content.webUrl;
            // Get the app name from the document. Sometimes this comes back empty.
            // If that happens, default to 'Office' as the application name
            var applicationName = content.app ||
                documentActionsMenuResources.officeApplicationName;
            // Use content.title unless the title is empty, then try fileName
            // If file name is also undefined, fallback to webUrl as the title since that is required for
            // performing this action.
            var title = content.title || content.fileName || webUrl;
            return OfficeActionCreators.createTodoAction(title, webUrl, 'Tasks', // This is the default list id for To Do.
            applicationName, {
                content: content,
                nextAction: {
                    appId: appId,
                    id: OfficeActionId.ViewTodoExternal,
                    mode: NextActionMode.Notification,
                },
                notificationOptions: notificationOptions,
            });
        },
    }, 
    // Success action for open a To Do in a new window. Not user facing.
    {
        intent: ActionIntent.ContentView,
        entities: [] /* Not visible in any kind of UX */,
        metadata: {
            appId: appId,
            name: documentActionsMenuResources.viewInToDoActionTitle,
            description: documentActionsMenuResources.viewInToDoActionTitle,
            id: OfficeActionId.ViewTodoExternal,
            notificationOptions: {
                loading: {
                    showLoadingIndicator: false,
                },
                error: {
                    message: documentActionsToastResources.viewToDoFailed,
                    actionText: documentActionsToastResources.openButtonText,
                },
            },
        },
        officeActionCreator: function (content) {
            if (!content.webUrl) {
                return undefined;
            }
            return OfficeActionCreators.createLaunchUrlAction(content.webUrl, '_blank', {
                // $TODO: The ToDo referral already exists in new Otel telemetry, but not in old telemetry constants for workload
                // For now, use 'ToDo', but will use 'Referral' when we migrate.
                referral: 'ToDo',
                referralPlatform: isHwa ? 'Native' : 'Web',
            });
        },
    });
    return actions;
};
export var getConvertToPdfActions = function (appId, hubActionsStrings, isHwa) {
    var documentActionsMenuResources = hubActionsStrings.documentActionsMenuResources, documentActionsToastResources = hubActionsStrings.documentActionsToastResources;
    if (!documentActionsMenuResources || !documentActionsToastResources) {
        return undefined;
    }
    var pdfActions = [];
    // In Embed workflow, always open created pdf externally.
    var nextActionAndResolvedDefinition = {
        nextAction: {
            appId: appId,
            id: OfficeActionId.ViewPdfExternal,
            mode: NextActionMode.Notification,
        },
        actionDefinition: {
            intent: ActionIntent.ContentView,
            entities: [] /* Not visible in any kind of UX */,
            metadata: {
                appId: appId,
                name: documentActionsMenuResources.viewPdfExternalActionTitle,
                description: documentActionsMenuResources.viewPdfExternalActionTitle,
                id: OfficeActionId.ViewPdfExternal,
                notificationOptions: {
                    loading: {
                        showLoadingIndicator: false,
                    },
                    error: {
                        message: documentActionsToastResources.viewPdfFailed,
                        actionText: documentActionsToastResources.openButtonText,
                    },
                },
            },
            officeActionCreator: function (content) {
                if (content.fileType !== FileType.Pdf ||
                    content.webUrl === undefined) {
                    return undefined;
                }
                return OfficeActionCreators.createLaunchUrlAction(content.webUrl, '_blank', {
                    referral: getReferralFromFileType(content.fileType),
                    referralPlatform: isHwa ? 'Native' : 'Web',
                });
            },
        },
    };
    pdfActions.push(nextActionAndResolvedDefinition.actionDefinition);
    // Create convert to PDF via API action
    var notificationOptions = {
        error: {
            message: documentActionsToastResources.convertToPdfFailed,
            actionText: documentActionsToastResources.closeButtonText,
        },
        loading: {
            showLoadingIndicator: true,
            loadingText: documentActionsToastResources.convertingToPdf,
        },
        success: {
            message: documentActionsToastResources.convertedToPdfSuccess,
            actionText: documentActionsToastResources.openButtonText,
        },
    };
    pdfActions.push({
        intent: ActionIntent.ContentEdit,
        entities: [{ type: ActionEntityType.Content }],
        metadata: {
            appId: appId,
            groupId: ContentActionGroup.MosApp,
            name: documentActionsMenuResources.convertToPdfActionTitle,
            description: documentActionsMenuResources.convertToPdfActionTitle,
            id: OfficeActionId.ConvertToPdf,
            icon: {
                type: ActionContextMenuItemIcon.Svg,
                iconName: 'PdfWorksSvgIcon',
            },
            notificationOptions: notificationOptions,
        },
        officeActionCreator: function (content) {
            if (!(content.isOneDriveForBusinessFile ||
                content.isSharePointFile) ||
                !content.driveId ||
                !content.docId ||
                !content.fileName) {
                return undefined;
            }
            // FileType must be word, excel or powerpoint
            if (!content.fileType ||
                (content.fileType !== FileType.Word &&
                    content.fileType !== FileType.Excel &&
                    content.fileType !== FileType.PowerPoint)) {
                return undefined;
            }
            // Fire convert to PDF action with drive and request/auth information
            var driveId = content.driveId, itemId = content.docId, extension = content.extension, fileName = content.fileName, isRedeemed = content.isRedeemed, isSharePointFile = content.isSharePointFile, sharePointIds = content.sharepointIds, webUrl = content.webUrl;
            // If a SharePointFile, sharePointIds is required for this action
            if (isSharePointFile && !sharePointIds) {
                return undefined;
            }
            return OfficeActionCreators.createConvertDriveItemAction(driveId, itemId, fileName, extension, 'pdf', isSharePointFile ? sharePointIds : undefined, isRedeemed, webUrl, {
                content: content,
                nextAction: nextActionAndResolvedDefinition.nextAction,
                notificationOptions: notificationOptions,
            });
        },
    });
    return pdfActions;
};
//# sourceMappingURL=getMosAppActionsForEmbed.js.map